import { useEffect, useRef } from 'preact/hooks';
import './Knot.css';

// The six-state live Knot. This is the BLESSED renderer (knot-signature
// prototype, the `dir==="knot"` path): a 2D-canvas woven 2:3 knot whose outer
// thread TUMBLES in 3D and carries a state-driven WAVEFORM ("the thread
// speaks"), with a voice-driven centre DOT. It replaces the old static-SVG
// glyph that only pulsed the whole shape's opacity/scale.
//
// States:
//   idle          — calm, just tumbling; no wave, dot at rest.
//   listening     — dot pulses with the (real or synthesised) voice amplitude,
//                    faint thread ripple, heartbeat ring.
//   understanding — a steadier, slower thread ripple.
//   proposing     — the thread speaks: fast, high-amplitude ripple.
//   confirmed     — turns GREEN; the ripple decays and a gold ripple flies out.
//   disabled      — dimmed, a single static frame, no motion.
//
// The talking/understanding/proposing/confirmed states are driven by the
// capture flow in a later chunk (R5); this component owns the full machine so
// R5 only has to set `state` and (optionally) feed a real `amp`.
export type KnotState =
  | 'idle'
  | 'listening'
  | 'understanding'
  | 'proposing'
  | 'confirmed'
  | 'disabled';

export interface KnotProps {
  /** The renderer state (see above). Defaults to the resting `idle`. */
  state?: KnotState;
  /** Voice amplitude 0..1. When omitted while `listening`, a lively amplitude
   *  is synthesised; a real mic feeds this in R5. Ignored outside listening. */
  amp?: number;
  size?: number;
}

const GOLD = '#d9a441';
const GREEN = '#199e70';

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// #rrggbb → rgba() at a given alpha (the palette hexes are always opaque
// 6-digit; no need to parse anything more general).
function rgba(hex: string, a: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
}

function glow(
  ctx: CanvasRenderingContext2D,
  col: string,
  cx: number,
  cy: number,
  r: number,
  alpha: number,
) {
  if (r <= 0 || alpha <= 0) return;
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
  grad.addColorStop(0, rgba(col, alpha));
  grad.addColorStop(1, rgba(col, 0));
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
}

function strokeArc(
  ctx: CanvasRenderingContext2D,
  col: string,
  cx: number,
  cy: number,
  r: number,
  alpha: number,
) {
  if (r <= 0 || alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.strokeStyle = col;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

// One frame of the knot. `staticFrame` collapses ALL motion (tumble, breathe,
// wave, dot pulse, rings) to the calm resting pose — used for both the
// disabled state and the prefers-reduced-motion path.
function renderFrame(
  ctx: CanvasRenderingContext2D,
  dpr: number,
  w: number,
  h: number,
  state: KnotState,
  t: number,
  stateT: number,
  amp: number,
  staticFrame: boolean,
) {
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);

  const cx = w / 2;
  const cy = h / 2;
  const R = Math.min(w, h) * 0.3;
  const col = state === 'confirmed' ? GREEN : GOLD;
  const dim = state === 'disabled' ? 0.35 : 1;
  ctx.globalAlpha = dim;

  // Ambient glow behind the whole shape.
  glow(ctx, col, cx, cy, R * 2.6, 0.07 + amp * 0.1);

  // ph advances the Lissajous phase → the knot tumbles in 3D.
  const ph = staticFrame ? 0 : t * 0.5;
  const scale = (staticFrame ? 1 : 1 + 0.022 * Math.sin(t * 1.1)) * R * 0.92;

  // Thread waveform amplitude + speed, by state.
  let waveA = 0;
  let speed = 4;
  if (!staticFrame) {
    if (state === 'listening') {
      waveA = 0.03 + amp * 0.035;
      speed = 4;
    } else if (state === 'understanding') {
      waveA = 0.12;
      speed = 3.2;
    } else if (state === 'proposing') {
      waveA = 0.18;
      speed = 8;
    } else if (state === 'confirmed') {
      waveA = 0.18 * Math.max(0, 1 - stateT * 2.4);
      speed = 8;
    }
    // idle: waveA stays 0 → calm, just tumbles.
  }

  // Two offset strands so it reads as WOVEN.
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  for (let strand = 0; strand < 2; strand++) {
    const off = strand ? 0.05 : 0.0;
    const strandAlpha = strand ? 0.28 : 1;
    ctx.globalAlpha = dim * strandAlpha;
    ctx.strokeStyle = col;
    ctx.lineWidth = strand ? 1.4 : 2.3;
    ctx.beginPath();
    for (let i = 0; i <= 240; i++) {
      const a = (i / 240) * 2 * Math.PI;
      const lx = Math.cos(2 * a + ph); // 2:3 Lissajous, tumbling
      const ly = Math.sin(3 * a + ph);
      let nx = 3 * Math.cos(3 * a + ph); // outward normal
      let ny = 2 * Math.sin(2 * a + ph);
      const nl = Math.hypot(nx, ny) || 1;
      nx /= nl;
      ny /= nl;
      const wave =
        waveA * (0.62 * Math.sin(7 * a - t * speed) + 0.38 * Math.sin(11 * a - t * speed * 1.35)) +
        off;
      const px = lx + nx * wave; // ripple runs along the thread
      const py = ly + ny * wave;
      const x = cx + px * scale;
      const y = cy + py * scale;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = dim;

  // Centre DOT = the pulse.
  let dotR = R * 0.17;
  if (!staticFrame) {
    if (state === 'listening') dotR = R * (0.15 + amp * 0.3); // pulses with the voice
    else if (state === 'proposing') dotR = R * 0.2;
  }
  glow(ctx, col, cx, cy, dotR * 2.8, 0.6);
  ctx.fillStyle = col;
  ctx.beginPath();
  ctx.arc(cx, cy, dotR, 0, Math.PI * 2);
  ctx.fill();

  if (!staticFrame && state === 'listening') {
    const p = (t * 1.25) % 1; // heartbeat ring
    const rp = dotR + p * R * 1.25;
    strokeArc(ctx, col, cx, cy, rp, Math.max(0, 0.5 - p * 0.5));
  }
  if (!staticFrame && state === 'confirmed') {
    const rp = R * (1 + stateT * 4); // gold ripple flies out on confirm
    strokeArc(ctx, GOLD, cx, cy, rp, Math.max(0, 0.8 - stateT * 1.6));
  }
  ctx.globalAlpha = 1;
}

export function Knot({ state = 'idle', amp, size = 40 }: KnotProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  // The live amp prop is read every frame WITHOUT restarting the rAF loop (a
  // real mic in R5 will update it continuously); state/size DO restart it.
  const ampPropRef = useRef<number | undefined>(amp);
  ampPropRef.current = amp;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    // No 2D context (e.g. the happy-dom test env): leave the element mounted,
    // draw nothing, don't crash.
    if (!ctx) return;

    const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
    const w = size;
    const h = size;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Static-frame path: disabled, or reduced-motion — one calm pose, no loop.
    if (state === 'disabled' || reduce) {
      renderFrame(ctx, dpr, w, h, state, 0, 0, 0, true);
      return;
    }

    let raf = 0;
    let smoothAmp = 0;
    const t0 = performance.now();
    let last = t0;

    const draw = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      const t = (now - t0) / 1000;
      const stateT = t; // effect restarts on state change, so t == time-in-state

      let target = 0;
      if (state === 'listening') {
        const provided = ampPropRef.current;
        target =
          provided != null
            ? clamp01(provided)
            : clamp01(0.45 + 0.32 * Math.sin(t * 7.1) + 0.23 * Math.sin(t * 13.7));
      }
      smoothAmp += (target - smoothAmp) * Math.min(1, dt * 10);

      renderFrame(ctx, dpr, w, h, state, t, stateT, smoothAmp, false);
      raf = requestAnimationFrame(draw);
    };

    raf = requestAnimationFrame(draw);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }, [state, size]);

  return (
    <canvas
      ref={canvasRef}
      class={`knot knot--${state}`}
      style={{ width: `${size}px`, height: `${size}px` }}
      aria-hidden="true"
    />
  );
}
